/**
 * The location slice of the register filters: a place (with a radius while it
 * is a point) or a drawn/saved map area. An anchor and an area are mutually
 * exclusive, exactly as they are on the wire. This file is the only place UI
 * location state becomes query parameters.
 */
import type { CalculationCardQuery, GeoSuggestion, LocationAnchor } from '../../api/types'

export type { LocationAnchor }

export interface LocationArea {
  name: string
  ring: [number, number][]
  /** Set only when the ring came from a saved area; the server resolves it by id. */
  areaId: string | null
}

export interface RegisterLocation {
  locationAnchor: LocationAnchor | null
  locationRadiusMiles: number
  locationArea: LocationArea | null
}

export function ringToParam(ring: [number, number][]): string {
  return ring.map(([lat, lng]) => `${lat.toFixed(6)},${lng.toFixed(6)}`).join('|')
}

export const RADIUS_OPTIONS = [0.25, 0.5, 1, 3, 5, 10, 15, 20] as const
export const DEFAULT_RADIUS_MILES = 3

export function radiusLabel(miles: number): string {
  if (miles === 0.25) return '+¼ mile'
  if (miles === 0.5) return '+½ mile'
  return `+${miles} mile${miles === 1 ? '' : 's'}`
}

export function toAnchor(suggestion: GeoSuggestion): LocationAnchor {
  const label = suggestion.sublabel && ['road', 'place'].includes(suggestion.kind) ? `${suggestion.label}, ${suggestion.sublabel}` : suggestion.label
  return { kind: suggestion.kind, selectionMode: suggestion.selectionMode, label, latitude: suggestion.latitude, longitude: suggestion.longitude, postcode: suggestion.postcode, outcode: suggestion.outcode, areaId: suggestion.areaId, provider: suggestion.provider }
}

export function anchorKindLabel(anchor: LocationAnchor): string {
  return anchor.kind.replaceAll('_', ' ')
}

export function exactFilter(anchor: LocationAnchor): Pick<CalculationCardQuery, 'postcode' | 'postcodeDistrict'> | null {
  if (anchor.kind === 'postcode' && anchor.postcode) return { postcode: anchor.postcode }
  if (anchor.kind === 'outcode' && anchor.outcode) return { postcodeDistrict: anchor.outcode }
  return null
}

export function radiusChoices(anchor: LocationAnchor): { value: string; label: string }[] {
  const exact = exactFilter(anchor)
  return [
    ...(exact ? [{ value: '0', label: anchor.kind === 'postcode' ? 'This postcode only' : 'This district only' }] : []),
    ...RADIUS_OPTIONS.map(miles => ({ value: String(miles), label: radiusLabel(miles) })),
  ]
}

export type LocationQuery = Pick<CalculationCardQuery, 'lat' | 'lng' | 'radiusMiles' | 'polygon' | 'areaId' | 'adminAreaId' | 'postcode' | 'postcodeDistrict'>

export function locationQuery(location: RegisterLocation): LocationQuery {
  const { locationAnchor: anchor, locationRadiusMiles: radiusMiles, locationArea: area } = location
  if (area) {
    if (area.areaId) return { areaId: area.areaId }
    return area.ring.length >= 3 ? { polygon: ringToParam(area.ring) } : {}
  }
  if (!anchor) return {}
  if (anchor.selectionMode === 'boundary') return anchor.areaId ? { adminAreaId: anchor.areaId } : {}
  if (radiusMiles === 0) {
    const exact = exactFilter(anchor)
    if (exact) return exact
  }
  return { lat: anchor.latitude, lng: anchor.longitude, radiusMiles: radiusMiles > 0 ? radiusMiles : DEFAULT_RADIUS_MILES }
}

export function hasLocation(location: RegisterLocation): boolean {
  return location.locationAnchor !== null || location.locationArea !== null
}

const ANCHOR_KINDS: LocationAnchor['kind'][] = ['postcode', 'outcode', 'place', 'road', 'address', 'admin_area']
const SELECTION_MODES: LocationAnchor['selectionMode'][] = ['radius', 'boundary']

function record(value: unknown): Record<string, unknown> | null { return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null }
function filledText(value: unknown): string | null { return typeof value === 'string' && value.trim() !== '' ? value : null }
function optionalText(value: unknown): string | null { return typeof value === 'string' && value !== '' ? value : null }
function finite(value: unknown): number | null { return typeof value === 'number' && Number.isFinite(value) ? value : null }

export function storedAnchor(value: unknown): LocationAnchor | null {
  const raw = record(value)
  if (!raw) return null
  const kind = ANCHOR_KINDS.find(c => c === raw.kind)
  const selectionMode = SELECTION_MODES.find(c => c === raw.selectionMode)
  const label = filledText(raw.label)
  const latitude = finite(raw.latitude)
  const longitude = finite(raw.longitude)
  if (!kind || !selectionMode || !label || latitude === null || longitude === null) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { kind, selectionMode, label, latitude, longitude, postcode: optionalText(raw.postcode), outcode: optionalText(raw.outcode), areaId: optionalText(raw.areaId), provider: optionalText(raw.provider) }
}

function storedRing(value: unknown): [number, number][] | null {
  if (!Array.isArray(value)) return null
  const ring: [number, number][] = []
  for (const point of value) {
    if (!Array.isArray(point) || point.length !== 2) return null
    const latitude = finite(point[0]); const longitude = finite(point[1])
    if (latitude === null || longitude === null) return null
    ring.push([latitude, longitude])
  }
  return ring.length >= 3 ? ring : null
}

export function storedArea(value: unknown): LocationArea | null {
  const raw = record(value)
  if (!raw) return null
  const areaId = optionalText(raw.areaId)
  const ring = storedRing(raw.ring)
  if (!ring && !areaId) return null
  return { name: filledText(raw.name) ?? 'Custom area', ring: ring ?? [], areaId }
}

function storedRadius(value: unknown, anchor: LocationAnchor | null): number {
  const miles = finite(value)
  if (miles === null) return DEFAULT_RADIUS_MILES
  if (miles === 0) return anchor && exactFilter(anchor) ? 0 : DEFAULT_RADIUS_MILES
  return RADIUS_OPTIONS.some(option => option === miles) ? miles : DEFAULT_RADIUS_MILES
}

/** Normalises the location keys of any stored payload; an area always wins. */
export function sanitizeStoredLocation(value: Record<string, unknown>): Record<string, unknown> {
  const locationArea = storedArea(value.locationArea)
  const locationAnchor = locationArea ? null : storedAnchor(value.locationAnchor)
  return { ...value, locationAnchor, locationArea, locationRadiusMiles: storedRadius(value.locationRadiusMiles, locationAnchor) }
}
